import { sortBy as _sortBy } from 'lodash'

import { Format } from 'services/Dates'
import { getCSV, getPositionName, getUserName, getProjectName, getDepartmentNames, getProjectCode } from 'services/Utilities'
import { store } from 'store/reducers'
import { coordinatesToString } from 'services/geoUtils'
import { insertIf } from 'common'
import { getPunchCards } from './utilities'

import { t } from 'translations'

export const exportPunchCardsCSV = async ({ filter = {}, sortBy }: { filter: Record<string, unknown>; sortBy: string }): Promise<void> => {
  const [punchCards, range] = await getPunchCards({ filter, sortBy })
  const {
    resources: { positionsById, projectsById },
    settings: { enablePunchGeolocation }
  } = store.getState() as MStore.RootState

  const fields = [
    'firstName',
    'lastName',
    'employeeNumber',
    'position',
    'date',
    'day',
    'shiftStart',
    'shiftEnd',
    'shiftTotal',
    'shiftTotalDec',
    'workedStart',
    'workedStartRounded',
    'workedEnd',
    'workedEndRounded',
    'workedTotal',
    'workedTotalDec',
    'difference',
    'differenceDec',
    'totalPlanned',
    'totalPlannedDec',
    'totalRegular',
    'totalRegularDec',
    'transfers',
    'breaks',
    'wagePremiums',
    'project',
    'projectCode',
    ...insertIf(enablePunchGeolocation, 'arrivalCoordinates', 'leaveCoordinates'),
    'additionalInfo',
    'comments',
    'approvedBy',
    'shiftId',
    'departments',
    'clients'
  ]

  const parsedPunchCards = punchCards.map(punchCard => {
    const arrivalComment = punchCard.arrivalComment ? `${t('manager.punch.ARRIVAL')}: ${punchCard.arrivalComment}` : null
    const leaveComment = punchCard.leaveComment ? `${t('manager.punch.LEAVE')}: ${punchCard.leaveComment}` : null
    const transferComments = punchCard.transfers
      .map(transfer =>
        transfer.comment
          ? `${transfer.toProject ? t('manager.punch.TRANSFER_PROJECT') : t('manager.punch.TRANSFER_POSITION')} - ${
              getPositionName(transfer.toPosition) || getProjectName(transfer.toProject)
            }: ${transfer.comment}`
          : null
      )
      .filter(Boolean)
    const parsedPunchCard = {
      ...punchCard,
      arrivalCoordinates: coordinatesToString(punchCard.arrivalCoordinates, 5),
      leaveCoordinates: coordinatesToString(punchCard.leaveCoordinates, 5),
      approvedBy: getUserName(punchCard.approvedBy),
      clients: projectsById[punchCard.project]?.clients?.map(client => client.name).join(', '),
      project: getProjectName(punchCard.project, { canBeNull: true }),
      projectCode: getProjectCode(punchCard.project, { canBeNull: true }),
      position: getPositionName(punchCard.positionId),
      departments: getDepartmentNames(positionsById[punchCard.positionId]?.departments, { delimiter: ', ' }),
      wagePremiums: (punchCard.wagePremiums || [])
        .map(({ earningCode, hours, amount, units }) => `${earningCode}${hours || amount || units ? ` (${hours || amount || units})` : ''}`)
        .join(', '),
      transfers: punchCard?.transfers
        .map(
          ({ toPosition, toProject, time }) =>
            `${toPosition != null ? getPositionName(toPosition) : getProjectName(toProject)} (${Format(time, { outFormat: 'time' })})`
        )
        .join(', '),
      breaks: (punchCard.breaks || [])
        .map(
          ({ duration, minutes, paid, name, reason, unplanned }) =>
            `${unplanned ? t('manager.punch.UNPLANNED', { reason }) : name}${paid ? ` ${t('manager.punch.PAID')}` : ''} (${duration || minutes})`
        )
        .join(', '),
      comments: [arrivalComment, ...transferComments, leaveComment].filter(comment => !!comment).join('\n')
    }
    return parsedPunchCard
  })

  getCSV(parsedPunchCards, fields, t('manager.punch.csv.CSV_NAME', { range }))
}
